// Host-side room setup: ruleset, how many seats are held for humans, and
// whether the wall is dealt from a drand beacon. The form itself lives in
// `network/roomOptions.ts` (pure, tested); this screen only edits it.
//
// On submit the screen stays put until the server answers, so a refused or
// unreachable connection is shown here rather than silently stranding the
// player on an empty lobby. Once `ROOM_CREATED` lands it hands off to the
// lobby screen, which the T16 join screen will share.

import type { MenuScreen, MahjongMenuContext } from '../types.js';
import type { RulesetId } from '../../network/protocol.js';
import { HUMAN_SEAT_OPTIONS } from '../../network/roomOptions.js';
import { createScreenShell, createMenuHeader, createCrestButton, el } from '@bn-games/ui-shell';
import { t } from '../../i18n/I18n.js';

const RULESET_OPTIONS: readonly RulesetId[] = ['simplified', 'classical'];
const VERIFIABLE_OPTIONS: readonly boolean[] = [false, true];

/**
 * Label above a row of `createCrestButton`s, the selected one carrying the
 * existing `primary` flag — the same no-new-CSS idiom `PlayMenu` uses for its
 * ruleset picker, so the two screens read alike.
 */
function choiceRow<T>(opts: {
  readonly label: string;
  readonly options: readonly T[];
  readonly value: T;
  readonly format: (value: T) => string;
  readonly onPick: (value: T) => void;
}): HTMLElement {
  const buttons = opts.options.map((option) =>
    createCrestButton({
      label: opts.format(option),
      small: true,
      primary: option === opts.value,
      onClick: () => {
        if (option === opts.value) return;
        opts.onPick(option);
      },
    }),
  );
  return el('div', {}, [
    el('div', { class: 'online-label', text: opts.label }),
    el('div', { class: 'button-row' }, buttons),
  ]);
}

export function createOnlineCreateScreen(): MenuScreen {
  let frame: HTMLElement | null = null;
  let root: HTMLElement | null = null;
  let unsubRoom: (() => void) | null = null;
  let submitted = false;

  function submit(ctx: MahjongMenuContext): void {
    submitted = true;
    ctx.online.createRoom();
    paint(ctx);
  }

  /** The server has answered: a code means the lobby, an error means stay. */
  function onRoomUpdate(ctx: MahjongMenuContext): void {
    if (!submitted) return;
    const info = ctx.online.getRoomInfo();
    if (info.roomCode !== null) {
      submitted = false;
      ctx.navigate('online');
      return;
    }
    if (info.state === 'error') submitted = false;
    paint(ctx);
  }

  function buildForm(ctx: MahjongMenuContext): HTMLElement {
    const form = ctx.online.getForm();
    const repaint = (): void => paint(ctx);
    return el('div', { class: 'online-center' }, [
      choiceRow<RulesetId>({
        label: t('online.create.rulesetLabel'),
        options: RULESET_OPTIONS,
        value: form.rulesetId,
        format: (id) => t(`settings.variants.${id}`),
        onPick: (rulesetId) => {
          ctx.online.setForm({ rulesetId });
          repaint();
        },
      }),
      choiceRow<number>({
        label: t('online.create.humansLabel'),
        options: HUMAN_SEAT_OPTIONS,
        value: form.humanSeats,
        format: (n) => String(n),
        onPick: (humanSeats) => {
          ctx.online.setForm({ humanSeats });
          repaint();
        },
      }),
      el('div', { class: 'online-hint', text: t('online.create.botsHint') }),
      choiceRow<boolean>({
        label: t('online.create.verifiableLabel'),
        options: VERIFIABLE_OPTIONS,
        value: form.verifiable,
        format: (on) => (on ? t('online.create.on') : t('online.create.off')),
        onPick: (verifiable) => {
          ctx.online.setForm({ verifiable });
          repaint();
        },
      }),
      form.verifiable
        ? el('div', { class: 'online-hint', text: t('online.create.verifiableHint') })
        : null,
    ]);
  }

  function buildStatus(ctx: MahjongMenuContext): HTMLElement | null {
    if (submitted) {
      return el('div', {
        class: 'online-status waiting-dots',
        text: t('online.create.connecting'),
      });
    }
    const info = ctx.online.getRoomInfo();
    if (info.state !== 'error') return null;
    return el('div', {
      class: 'online-status',
      text: info.errorMessage ?? t('online.create.error'),
    });
  }

  function paint(ctx: MahjongMenuContext): void {
    if (root === null) return;
    while (root.firstChild) root.removeChild(root.firstChild);
    const shell = createScreenShell();
    frame = shell.frame;
    const body = shell.body;

    body.appendChild(createMenuHeader({ title: t('online.create.title') }));

    const center = buildForm(ctx);
    const status = buildStatus(ctx);
    if (status !== null) center.appendChild(status);
    center.appendChild(
      el('div', { class: 'button-stack' }, [
        createCrestButton({
          label: t('online.create.submit'),
          primary: true,
          disabled: submitted,
          onClick: () => submit(ctx),
        }),
      ]),
    );
    body.appendChild(center);

    body.appendChild(
      el('div', { class: 'button-row back-button-row' }, [
        createCrestButton({
          label: t('common.back'),
          small: true,
          onClick: () => {
            ctx.online.leaveRoom();
            ctx.navigate('play');
          },
        }),
      ]),
    );
    root.appendChild(frame);
  }

  return {
    mount(rootEl, ctx) {
      root = rootEl;
      submitted = false;
      unsubRoom = ctx.online.onRoomUpdate(() => onRoomUpdate(ctx));
      paint(ctx);
    },
    unmount() {
      unsubRoom?.();
      unsubRoom = null;
      frame?.remove();
      frame = null;
      root = null;
      submitted = false;
    },
  };
}
